import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  useToast,
  Image,
  IconButton,
  Spinner,
  Flex,
  Badge,
  Card,
  CardBody,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  Textarea,
  Heading,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  AlertDescription,
  Tooltip,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  Upload,
  User,
  DollarSign,
  FileText,
  X,
  ShieldCheck,
  ArrowLeft,
  Home,
  ChevronRight,
  Plus,
  AlertCircle,
} from "lucide-react";
import { getData, postData, updateData } from "../../redux/actions/common.action";
import {
  ADMISSION_SECTION_KEYS,
  hasAdmissionMenu,
  hasAdmissionSection,
} from "../../utils/admissionAccess";
import { BASE_URL } from "../../config/RequestMethod";
import axios from "axios";
import Cookies from "js-cookie";

const AdmissionForm = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Edit mode detection
  const editItem = location.state as any;
  const isEditMode = !!editItem?._id;

  const { branches, courses, users, accounts, signinuser } = useSelector(
    (state: any) => state.common,
  );

  useEffect(() => {
    if (!signinuser) return;
    if (!hasAdmissionMenu(signinuser)) {
      navigate("/unauthorized", { replace: true });
      return;
    }
    if (
      isEditMode &&
      !hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.EDIT)
    ) {
      toast({
        title: "No access",
        description: "You do not have permission to edit admissions.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      navigate("/admission-dashboard", { replace: true });
      return;
    }
    if (
      !isEditMode &&
      !hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.CREATE)
    ) {
      toast({
        title: "No access",
        description: "You do not have permission to create admissions.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      navigate("/admission-dashboard", { replace: true });
    }
  }, [signinuser, isEditMode, navigate, toast]);

  const [isUploading, setIsUploading] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [showPurchasedOnlyWarning, setShowPurchasedOnlyWarning] =
    useState(false);

  // Prevent stale API responses from reopening the dropdown after input is cleared.
  const latestSearchQueryRef = useRef<string>("");

  const normalizeSessionMonth = (v: any) => {
    if (!v) return "";
    // If stored as "MM/YYYY", convert to "YYYY-MM" for <input type="month" />
    if (typeof v === "string" && /^\d{2}\/\d{4}$/.test(v)) {
      const [mm, yyyy] = v.split("/");
      return `${yyyy}-${mm}`;
    }
    // If stored already as "YYYY-MM"
    if (typeof v === "string" && /^\d{4}-\d{2}$/.test(v)) return v;
    return v;
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    getValues,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    defaultValues: {
      admDate: new Date().toISOString().split("T")[0],
      dateOfBirth: "",
      fathersName: "",
      sessionFrom: "",
      sessionTo: "",
      highestGraduation: "",
      paymentType: "Full",
      paymentMode: "Manual",
      emiCount: 0,
      downPayment: 0,
      totalCoursePrice: 0,
      installDetails: [],
      admStatus: "Pending",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "installDetails",
  });

  const watchPaymentType = watch("paymentType");
  const watchTotalPrice = watch("totalCoursePrice");
  const watchDownPayment = watch("downPayment");
  const watchEmiCount = watch("emiCount");
  const watchCourseId = watch("courseId");
  const watchInstallments = watch("installDetails");
  const watchBranchId = watch("branchId");
  const watchSessionFrom = watch("sessionFrom");
  const watchSessionTo = watch("sessionTo");
  const watchFrontAadhar = watch("frontAadhar");
  const watchPanCard = watch("panCard");

  useEffect(() => {
    dispatch(getData("branch"));
    dispatch(getData("course"));
    dispatch(getData("user"));
    dispatch(getData("account"));
  }, [dispatch]);

  // Populate form fields in edit mode
  useEffect(() => {
    if (!isEditMode || !editItem) return;

    // Set a placeholder selectedLead so form sections are visible
    setSelectedLead({ _id: editItem.leadId, Name: editItem.name });

    // Scalar fields
    setValue("leadId", editItem.leadId || "");
    setValue("name", editItem.name || "");
    setValue("email", editItem.email || "");
    setValue("mobileNo", editItem.mobileNo || "");
    setValue("altMobileNo", editItem.altMobileNo || "");
    setValue("address", editItem.address || "");
    setValue("admDate", editItem.admDate ? new Date(editItem.admDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
    setValue(
      "dateOfBirth",
      editItem.dateOfBirth
        ? new Date(editItem.dateOfBirth).toISOString().split("T")[0]
        : "",
    );
    setValue("fathersName", editItem.fathersName || "");
    setValue(
      "sessionFrom",
      normalizeSessionMonth(editItem.sessionFrom || ""),
    );
    setValue(
      "sessionTo",
      normalizeSessionMonth(editItem.sessionTo || ""),
    );
    setValue("highestGraduation", editItem.highestGraduation || "");
    setValue("remark", editItem.remark || "");
    setValue("admStatus", editItem.admStatus || "Pending");

    // Payment fields
    setValue("paymentType", editItem.paymentType || "Full");
    setValue("paymentMode", editItem.paymentMode || "Manual");
    setValue("totalCoursePrice", Number(editItem.totalCoursePrice || 0));
    setValue("downPayment", Number(editItem.downPayment || 0));
    setValue("emiCount", Number(editItem.emiCount || 0));
    setValue("paymentAccount", editItem.paymentAccount || "");

    // Populated object fields -> extract _id
    const branchId = typeof editItem.branchId === "object" ? editItem.branchId?._id : editItem.branchId;
    const courseId = typeof editItem.courseId === "object" ? editItem.courseId?._id : editItem.courseId;
    const counsellorId = typeof editItem.counsellorId === "object" ? editItem.counsellorId?._id : editItem.counsellorId;
    const approverId = typeof editItem.approverId === "object" ? editItem.approverId?._id : editItem.approverId;

    if (branchId) setValue("branchId", branchId);
    if (courseId) setValue("courseId", courseId);
    if (approverId) setValue("approverId", approverId);

    // Set counsellor after branch is set
    if (counsellorId) {
      setTimeout(() => {
        setValue("counsellorId", counsellorId);
      }, 200);
    }

    // KYC documents
    if (editItem.profilePic) setValue("profilePic", editItem.profilePic);
    if (editItem.frontAadhar) setValue("frontAadhar", editItem.frontAadhar);
    if (editItem.backAadhar) setValue("backAadhar", editItem.backAadhar);
    if (editItem.panCard) setValue("panCard", editItem.panCard);

    // Install details
    if (editItem.installDetails?.length > 0) {
      replace(editItem.installDetails);
    }
  }, [isEditMode]);

  const branchOptions = useMemo(
    () => branches?.map((b: any) => ({ label: b.BranchName, value: b._id })),
    [branches],
  );

  const courseOptions = useMemo(
    () => courses?.map((c: any) => ({ label: c.CourseName, value: c._id })),
    [courses],
  );

  const accountOptions = useMemo(
    () =>
      accounts?.map((acc: any) => ({
        label: `${acc.bankName} - ${acc.accountNo?.slice(-4)}`,
        value: acc._id,
      })),
    [accounts],
  );

  const filteredCounsellors = useMemo(() => {
    return users
      ?.filter(
        (u: any) =>
          u.active !== false &&
          u.UserType === 3 &&
          u.Branch?._id === watchBranchId,
      )
      ?.map((u: any) => ({ label: u.Name, value: u._id }));
  }, [users, watchBranchId]);

  // Approver selection: Users with "Approver" designation
  const approverOptions = useMemo(() => {
    return users
      ?.filter((u: any) => u.active !== false && u.designation === "Approver")
      ?.map((u: any) => ({ label: u.Name, value: u._id }));
  }, [users]);

  useEffect(() => {
    setValue("counsellorId", "");
  }, [watchBranchId, setValue]);

  // Auto-clear KYC error as soon as either Aadhar or PAN is uploaded
  useEffect(() => {
    if (watchFrontAadhar || watchPanCard) {
      clearErrors("kycDocument");
    }
  }, [watchFrontAadhar, watchPanCard, clearErrors]);

  const handleBack = () => {
    navigate("/admission-list");
  };

  const handleSearchLead = async (query: string) => {
    setSearchQuery(query);
    latestSearchQueryRef.current = query;
    if (query.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const token = Cookies.get("token");
      const response = await axios.get(
        `${BASE_URL}lead?keyword=${query}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // If the user has typed something else since this request started, ignore this response.
      if (latestSearchQueryRef.current !== query) return;
      setSearchResults(response.data.Data || []);
    } catch (error) {
      // Ignore stale errors too.
      if (latestSearchQueryRef.current !== query) return;
      console.error(error);
    } finally {
      if (latestSearchQueryRef.current === query) setIsSearching(false);
    }
  };

  const selectLead = (lead: any) => {
    // Check if lead status is "Purchased"
    if (lead.StatusInfo?.StatusName !== "Purchased") {
      setShowPurchasedOnlyWarning(true);
      setTimeout(() => {
        setShowPurchasedOnlyWarning(false);
      }, 4000);
      return;
    }

    setSelectedLead(lead);
    setSearchResults([]);
    setSearchQuery("");
    setShowPurchasedOnlyWarning(false);

    setValue("leadId", lead._id);
    setValue("name", lead.Name || "");
    setValue("email", lead.Email || "");
    setValue("mobileNo", lead.Phone1 || "");
    setValue("altMobileNo", lead.Phone2 || "");
    setValue("address", lead.location || "");

    if (lead.BranchInfo?._id) {
      setValue("branchId", lead.BranchInfo._id);
    }

    if (lead.CourseInfo?._id) {
      setValue("courseId", lead.CourseInfo._id);
      setValue("totalCoursePrice", Number(lead.CourseInfo.CourseValue || 0));
    }

    // Set counsellor after branch is set - with proper handling
    if (lead.AssignInfo?._id) {
      // Use setTimeout to ensure branch is updated first
      setTimeout(() => {
        setValue("counsellorId", lead.AssignInfo._id);
      }, 100);
    }
  };

  const generateEmiSchedule = useCallback(() => {
    const count = Number(watchEmiCount);
    if (watchPaymentType !== "Installment" || count <= 0) {
      replace([]);
      return;
    }
    const principal =
      (Number(watchTotalPrice) || 0) - (Number(watchDownPayment) || 0);
    const baseAmount = Math.floor(principal / count);
    const remainder = principal % count;

    const newEmis = Array.from({ length: count }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() + (i + 1));
      return {
        label: `EMI ${i + 1}`,
        amount: i === count - 1 ? baseAmount + remainder : baseAmount,
        installDate: date.toISOString().split("T")[0],
        isPaid: false,
        paidDate: null,
        remark: "",
        updatedBy: signinuser?._id || null,
      };
    });
    replace(newEmis);
  }, [
    watchPaymentType,
    watchEmiCount,
    watchTotalPrice,
    watchDownPayment,
    replace,
    signinuser,
  ]);

  useEffect(() => {
    generateEmiSchedule();
  }, [watchEmiCount, watchPaymentType, watchTotalPrice, watchDownPayment]);

  const updateManualEmi = (index: number, value: string) => {
    const newVal = Number(value);
    const principal =
      (Number(watchTotalPrice) || 0) - (Number(watchDownPayment) || 0);
    let currentTotalExcludingLast = 0;
    watchInstallments.forEach((emi: any, i: number) => {
      if (i !== index && i !== watchInstallments.length - 1)
        currentTotalExcludingLast += Number(emi.amount);
    });
    const newRemainingAfterThisChange =
      principal - (currentTotalExcludingLast + newVal);
    if (newRemainingAfterThisChange >= 0) {
      setValue(`installDetails.${index}.amount`, newVal);
      setValue(`installDetails.${index}.updatedBy`, signinuser?._id || null);
      setValue(
        `installDetails.${watchInstallments.length - 1}.amount`,
        newRemainingAfterThisChange,
      );
    }
  };

  const handleUpload = (e: any, fieldName: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(fieldName);
    const formData = new FormData();
    formData.append("file", file);
    dispatch(postData(formData, "upload-image", navigate, toast))
      .then((data: any) => {
        setValue(fieldName, data?.imageUrl);
      })
      .finally(() => setIsUploading(null));
  };

  const onSubmit = async (data: any) => {
    // KYC: either Aadhar or PAN is required
    if (!data.frontAadhar && !data.panCard) {
      setError("kycDocument", {
        type: "manual",
        message: "Either Aadhar Card or PAN Card is required",
      });
      // Scroll to KYC section
      document.getElementById("kyc-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Validate that a lead has been selected (only in create mode)
    if (!isEditMode && !selectedLead) {
      toast({
        title: "Lead Selection Required",
        description:
          "Please select a purchased lead before submitting the admission form.",
        status: "error",
        position: "top-right",
        duration: 3000,
      });
      return;
    }

    const totalCoursePrice = Number(data.totalCoursePrice);
    const downPayment = Number(data.downPayment);
    const emiCount = Number(data.emiCount);

    if (data.paymentType === "Installment") {
      const totalScheduled =
        data.installDetails.reduce(
          (acc: number, curr: any) => acc + Number(curr.amount),
          0,
        ) + downPayment;

      if (Math.abs(totalScheduled - totalCoursePrice) > 1) {
        toast({
          title: "Payment Mismatch",
          description: `Total: ${totalScheduled} vs Course Price: ${totalCoursePrice}`,
          status: "error",
          duration: 5000,
          position: "top-right",
        });
        return;
      }
    }

    const finalPayload: any = {
      ...data,
      totalCoursePrice,
      downPayment,
      emiCount,
      paymentAccount:
        data.paymentMode === "Manual" ? data.paymentAccount : null,
    };

    // For Full payment type, NEVER include installDetails in the payload.
    // The installDetails for Full payment are managed exclusively by the payment
    // recording flow (manual update / portal) — they must never be overwritten
    // by the admission edit form, otherwise the payment tracking entry gets lost.
    if (data.paymentType === "Full") {
      delete finalPayload.installDetails;
    } else {
      finalPayload.installDetails = data.installDetails.map((emi: any) => ({
        ...emi,
        amount: Number(emi.amount),
      }));
    }

    if (isEditMode) {
      // Reset status to Pending so it goes through approval again (important for rejected admissions)
      finalPayload.admStatus = "Pending";
      return dispatch(updateData(editItem._id, "admission", finalPayload, navigate, toast));
    } else {
      return dispatch(postData(finalPayload, "admission", navigate, toast));
    }
  };

  const handleChangeLead = () => {
    setSelectedLead(null);
    setValue("leadId", "");
    setSearchQuery("");
    // Reset form fields but keep the default values
    reset({
      admDate: new Date().toISOString().split("T")[0],
      dateOfBirth: "",
      fathersName: "",
      sessionFrom: "",
      sessionTo: "",
      highestGraduation: "",
      paymentType: "Full",
      paymentMode: "Manual",
      emiCount: 0,
      downPayment: 0,
      totalCoursePrice: 0,
      installDetails: [],
      admStatus: "Pending",
    });
  };

  return (
    <Box
      p={3}
      minH="100vh"
      fontSize="sm"
      bgGradient="linear-gradient(135deg, #EEF2FF 0%, #F8FAFC 45%, #F3F4F6 100%)"
    >
      {/* Header Section with Back Button and Breadcrumb */}
      <VStack align="start" spacing={2} mb={6}>
        <Heading size="sm" color="gray.700" mb={1}>
          {isEditMode ? "Edit Admission" : "Create New Admission"}
        </Heading>
        <Button
          leftIcon={<ArrowLeft size={16} />}
          onClick={handleBack}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          fontWeight="medium"
          fontSize="sm"
          _hover={{
            bg: "blue.50",
            transform: "translateX(-4px)",
          }}
          transition="all 0.2s"
        >
          Back
        </Button>

        <Breadcrumb
          spacing={2}
          separator={<ChevronRight size={12} color="#9CA3AF" />}
          fontSize="xs"
          color="gray.600"
          display={{ base: "none", md: "flex" }}
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={handleBack}
              display="flex"
              alignItems="center"
              gap={1}
              _hover={{ color: "blue.500" }}
              cursor="pointer"
            >
              <Home size={12} />
              <Text>Admissions</Text>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink
              display="flex"
              alignItems="center"
              gap={1}
              color="blue.500"
              fontWeight="medium"
            >
              <Plus size={12} />
              <Text>{isEditMode ? "Edit Admission" : "New Admission"}</Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </VStack>

      {/* Note: validations are shown inline under fields; the “Purchased lead” hint is shown via hover icon */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={3} align="stretch">
          {/* Lead Selection Card - Hidden in edit mode */}
          {!isEditMode && (
          <Card variant="outline" size="sm" borderColor="blue.400" borderWidth="1px" boxShadow="sm">
            <CardBody p={2} bg="white">
              {/* Header row */}
              <HStack spacing={2} mb={2}>
                <Box p={1} bg="blue.500" borderRadius="md" flexShrink={0}>
                  <Search size={13} color="white" />
                </Box>
                <Text fontSize="xs" fontWeight="700" color="gray.700">
                  Search leads for admission
                </Text>
                <Tooltip
                  label={
                    <Box
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.200"
                      borderRadius="md"
                      p={2}
                      maxW="260px"
                    >
                      <VStack align="start" spacing={1}>
                        <Text fontSize="10px" fontWeight="900" color="orange.800">
                          Admission conversion rules
                        </Text>
                        <Text fontSize="10px" color="orange.700">
                          • Only leads with status{" "}
                          <Text as="span" fontWeight="800">Purchased</Text>{" "}
                          can be converted to admission
                        </Text>
                        <Text fontSize="10px" color="orange.700">
                          • Non-purchased leads are blocked
                        </Text>
                      </VStack>
                    </Box>
                  }
                  hasArrow
                  placement="top"
                >
                  <Box cursor="help" flexShrink={0}>
                    <AlertCircle size={13} color="#F97316" />
                  </Box>
                </Tooltip>
                {selectedLead && (
                  <Badge colorScheme="green" variant="solid" fontSize="9px" ml="auto">
                    ✓ Selected
                  </Badge>
                )}
              </HStack>

              {!selectedLead && (
                <Box position="relative" w="100%">
                  <InputGroup size="sm">
                    <Input
                      size="sm"
                      placeholder="Search leads for admission..."
                      value={searchQuery}
                      onChange={(e) => handleSearchLead(e.target.value)}
                      borderRadius="md"
                      bg="gray.50"
                      _focus={{ bg: "white", borderColor: "blue.400" }}
                      fontWeight="500"
                      fontSize="xs"
                    />
                    <InputRightElement>
                      {isSearching ? (
                        <Spinner size="sm" color="blue.500" />
                      ) : (
                        <Search size={14} color="#3182CE" />
                      )}
                    </InputRightElement>
                  </InputGroup>

                  {searchQuery.length >= 3 && searchResults.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      shadow="xl"
                      zIndex={20}
                      borderRadius="lg"
                      mt={1}
                      border="1px solid"
                      borderColor="blue.200"
                      maxH="340px"
                      overflowY="auto"
                    >
                      {searchResults.map((l) => {
                        const isPurchased =
                          l.StatusInfo?.StatusName === "Purchased";
                        return (
                          <Box
                            key={l._id}
                            p={2}
                            _hover={{
                              bg: isPurchased ? "blue.50" : "gray.100",
                              cursor: isPurchased ? "pointer" : "not-allowed",
                            }}
                            onClick={() => isPurchased && selectLead(l)}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            opacity={isPurchased ? 1 : 0.6}
                            transition="all 0.15s"
                          >
                            <HStack spacing={2} justify="space-between">
                              <VStack align="start" spacing={0} flex={1}>
                                <HStack spacing={2}>
                                  <Badge colorScheme="blue" fontSize="8px" px={1}>
                                    {l.UID}
                                  </Badge>
                                  <Text fontSize="xs" fontWeight="700" color="gray.800">
                                    {l.Name}
                                  </Text>
                                </HStack>
                                <Text fontSize="10px" color="gray.500">
                                  📧 {l.Email}
                                </Text>
                                <Text fontSize="10px" color="gray.500">
                                  📱 {l.Phone1}
                                </Text>
                                {l.CourseInfo && (
                                  <Text fontSize="10px" color="blue.600" fontWeight="600">
                                    💼 {l.CourseInfo.CourseName} — ₹{l.CourseInfo.CourseValue}
                                  </Text>
                                )}
                              </VStack>
                              <Badge
                                colorScheme={isPurchased ? "green" : "orange"}
                                variant="subtle"
                                fontSize="9px"
                                px={2}
                                py={1}
                                borderRadius="full"
                                flexShrink={0}
                              >
                                {l.StatusInfo?.StatusName || "Pending"}
                              </Badge>
                            </HStack>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {searchQuery.length > 0 &&
                    searchQuery.length < 3 &&
                    !isSearching && (
                      <Box p={1} mt={1} bg="yellow.50" borderRadius="md">
                        <Text fontSize="10px" color="yellow.700" fontWeight="500">
                          ⚠️ Enter at least 3 characters to search
                        </Text>
                      </Box>
                    )}
                </Box>
              )}

              {selectedLead && (
                <Box
                  p={4}
                  bg="white"
                  borderRadius="lg"
                  border="2px solid"
                  borderColor="green.400"
                  mt={3}
                >
                  <HStack justify="space-between" align="start">
                    <VStack align="start" spacing={2} flex={1}>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue">{selectedLead.UID}</Badge>
                        <Heading size="sm" color="green.700">
                          {selectedLead.Name}
                        </Heading>
                      </HStack>
                      <VStack align="start" spacing={1} fontSize="xs" color="gray.600">
                        <Text>📧 {selectedLead.Email}</Text>
                        <Text>📱 {selectedLead.Phone1}</Text>
                        {selectedLead.CourseInfo && (
                          <Text fontWeight="bold" color="blue.600">
                            💼 {selectedLead.CourseInfo.CourseName} - ₹
                            {selectedLead.CourseInfo.CourseValue}
                          </Text>
                        )}
                      </VStack>
                    </VStack>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={handleChangeLead}
                      leftIcon={<X size={14} />}
                    >
                      Change
                    </Button>
                  </HStack>
                </Box>
              )}
            </CardBody>
          </Card>
          )}

          {/* Student Information Card - Only visible after lead selection or in edit mode */}
          {(selectedLead || isEditMode) && (
            <Card variant="outline" size="sm" bg="white" boxShadow="sm">
              <CardBody p={3}>
                <Flex align="center" gap={2} mb={3}>
                  <Box p={2} bg="blue.50" borderRadius="lg">
                    <User size={16} color="#3182CE" />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Heading size="xs">Student Information</Heading>
                    <Text fontSize="xs" color="gray.500">
                      Modify if needed
                    </Text>
                  </VStack>
                </Flex>

                <SimpleGrid columns={{ base: 2, md: 5 }} gap={3}>
                  <FormControl isRequired isInvalid={!!errors.name}>
                    <FormLabel fontSize="xs" mb={1}>
                      Full Name
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="Full Name"
                      {...register("name", {
                        required: "Name is required",
                        minLength: { value: 2, message: "Name must be at least 2 characters" },
                      })}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errors.name?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel fontSize="xs" mb={1}>
                      Email
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="Email"
                      {...register("email", {
                        required: "Email is required",
                        validate: (v) => {
                          if (!v) return true;
                          // simple RFC-ish email validation
                          return (
                            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
                            "Invalid email format"
                          );
                        },
                      })}
                    />
                    <FormErrorMessage>
                      {errors.email?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.mobileNo}>
                    <FormLabel fontSize="xs" mb={1}>
                      Mobile
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      placeholder="Mobile"
                      {...register("mobileNo", {
                        required: "Mobile is required",
                        validate: (v) => {
                          if (v === null || v === undefined || v === "")
                            return true;
                          const s = String(v);
                          return (
                            /^\d{10}$/.test(s) || "Mobile must be 10 digits"
                          );
                        },
                      })}
                    />
                    <FormErrorMessage>
                      {errors?.mobileNo?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>
                      Alt Mobile
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      placeholder="Alt Mobile"
                      {...register("altMobileNo")}
                    />
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.admDate}>
                    <FormLabel fontSize="xs" mb={1}>
                      Adm Date
                    </FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      {...register("admDate", {
                        required: "Admission date is required",
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.admDate?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.dateOfBirth}
                    // keep optional styling but show errors if invalid
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Date of Birth
                    </FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      {...register("dateOfBirth", {
                        validate: (v) => {
                          if (!v) return true;
                          const selected = new Date(v);
                          const today = new Date();
                          // reset time to avoid timezone edge cases
                          selected.setHours(0, 0, 0, 0);
                          today.setHours(0, 0, 0, 0);
                          return selected <= today || "Date of birth must be in the past";
                        },
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.dateOfBirth?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.fathersName}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Father's Name
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="Father's Name"
                      {...register("fathersName", {
                        validate: (v) => {
                          if (!v) return true;
                          return v.trim().length >= 2 || "Father's name is too short";
                        },
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.fathersName?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    gridColumn={{ md: "span 2" }}
                    isInvalid={!!errors.sessionFrom || !!errors.sessionTo}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Session / Batch (From - To)
                    </FormLabel>
                    <HStack spacing={2}>
                      <Input
                        size="sm"
                        type="month"
                        {...register("sessionFrom", {
                          // re-validate sessionTo whenever sessionFrom changes
                          deps: ["sessionTo"],
                          validate: (v) => {
                            const to = getValues("sessionTo");
                            if (!v && !to) return true;
                            if (v && !to) return "Batch To is required when Batch From is set";
                            if (!v && to) return true; // sessionTo can exist without sessionFrom
                            // both filled — compare
                            return v <= to || "Batch From must be ≤ Batch To";
                          },
                        })}
                      />
                      <Input
                        size="sm"
                        type="month"
                        {...register("sessionTo", {
                          // re-validate sessionFrom whenever sessionTo changes
                          deps: ["sessionFrom"],
                          validate: (v) => {
                            const from = getValues("sessionFrom");
                            if (!v && !from) return true;
                            if (!v && from) return "Batch To is required when Batch From is set";
                            if (v && !from) return true; // sessionFrom not required by itself
                            // both filled — compare
                            return from <= v || "Batch To must be ≥ Batch From";
                          },
                        })}
                      />
                    </HStack>
                    {(errors.sessionFrom?.message || errors.sessionTo?.message) && (
                      <FormErrorMessage color="red.500" fontSize="xs">
                        {errors.sessionFrom?.message?.toString() ||
                          errors.sessionTo?.message?.toString()}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isInvalid={!!errors.highestGraduation}>
                    <FormLabel fontSize="xs" mb={1}>
                      Highest Graduation
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g., B.Com, M.Sc"
                      {...register("highestGraduation", {
                        validate: (v) => {
                          if (!v) return true;
                          return (
                            v.trim().length >= 2 || "Highest graduation is too short"
                          );
                        },
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.highestGraduation?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.branchId}>
                    <FormLabel fontSize="xs" mb={1}>
                      Branch
                    </FormLabel>
                    <Controller
                      control={control}
                      name="branchId"
                      rules={{ required: "Branch is required" }}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          ref={ref}
                          size="sm"
                          placeholder="Select branch"
                          options={branchOptions}
                          value={branchOptions?.find(
                            (opt: any) => opt.value === value,
                          )}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.branchId?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired={!!watchBranchId}
                    isDisabled={!watchBranchId}
                    isInvalid={!!errors.counsellorId}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Counsellor
                    </FormLabel>
                    <Controller
                      control={control}
                      name="counsellorId"
                      rules={{ required: watchBranchId ? "Counsellor is required" : false }}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          ref={ref}
                          size="sm"
                          isDisabled={!watchBranchId}
                          placeholder={
                            watchBranchId
                              ? "Select counsellor"
                              : "Select branch first"
                          }
                          options={filteredCounsellors}
                          value={filteredCounsellors?.find(
                            (opt: any) => opt.value === value,
                          )}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.counsellorId?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.address} gridColumn={{ md: "span 2" }}>
                    <FormLabel fontSize="xs" mb={1}>
                      Address
                    </FormLabel>
                    <Textarea
                      size="sm"
                      placeholder="Address"
                      {...register("address", {
                        required: "Address is required",
                        minLength: { value: 5, message: "Address must be at least 5 characters" },
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.address?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </CardBody>
            </Card>
          )}

          {/* Approval Section - Only visible after lead selection or in edit mode */}
          {(selectedLead || isEditMode) && (
            <Card
              variant="outline"
              size="sm"
              borderColor="blue.200"
              bg="blue.50"
            >
              <CardBody p={3}>
                <HStack mb={2}>
                  <ShieldCheck size={16} color="#064E98" />
                  <Heading size="xs">Approval Configuration</Heading>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                  <FormControl isRequired isInvalid={!!errors.approverId}>
                    <FormLabel fontSize="xs" mb={1}>
                      Assign Approver
                    </FormLabel>
                    <Controller
                      control={control}
                      name="approverId"
                      rules={{ required: "Approver is required" }}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          ref={ref}
                          size="sm"
                          placeholder="Select authorized approver"
                          options={approverOptions}
                          value={approverOptions?.find(
                            (opt: any) => opt.value === value,
                          )}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.approverId?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <Box>
                    <Text fontSize="xs" mb={1} fontWeight="bold" color="gray.600">
                      Initial Status
                    </Text>
                    <Badge colorScheme="orange" variant="solid">
                      PENDING APPROVAL
                    </Badge>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>
          )}

          {/* Payment Configuration - Only visible after lead selection or in edit mode */}
          {(selectedLead || isEditMode) && (
            <Card variant="outline" size="sm" bg="white" boxShadow="sm">
              <CardBody p={3}>
                <HStack mb={2}>
                  <DollarSign size={16} color="#059669" />
                  <Heading size="xs">Payment Configuration</Heading>
                </HStack>
                <SimpleGrid
                  columns={{ base: 1, md: 5 }}
                  gap={3}
                  mb={watchPaymentType === "Installment" ? 3 : 0}
                >
                  <FormControl
                    isRequired
                    isInvalid={!!errors.courseId}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Course
                    </FormLabel>
                    <Controller
                      control={control}
                      name="courseId"
                      rules={{ required: "Course is required" }}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          ref={ref}
                          size="sm"
                          placeholder="Select course"
                          options={courseOptions}
                          value={courseOptions?.find(
                            (opt: any) => opt.value === value,
                          )}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.courseId?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    isRequired
                    isInvalid={!!errors.totalCoursePrice}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Total Price
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      placeholder="0"
                      {...register("totalCoursePrice", {
                        required: "Total price is required",
                        valueAsNumber: true,
                        validate: (v) =>
                          v > 0 || "Total price must be greater than 0",
                      })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.totalCoursePrice?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="xs" mb={1}>
                      Type
                    </FormLabel>
                    <Controller
                      control={control}
                      name="paymentType"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          size="sm"
                          options={[
                            { label: "Full", value: "Full" },
                            {
                              label: "Installment",
                              value: "Installment",
                            },
                          ]}
                          value={{ label: value, value: value }}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>
                      Mode
                    </FormLabel>
                    <Controller
                      control={control}
                      name="paymentMode"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          size="sm"
                          options={[
                            { label: "Manual", value: "Manual" },
                            {
                              label: "Razorpay",
                              value: "auto_razorpay",
                            },
                            { label: "PayPal", value: "auto_paypal" },
                          ]}
                          value={[
                            { label: "Manual", value: "Manual" },
                            {
                              label: "Razorpay",
                              value: "auto_razorpay",
                            },
                            { label: "PayPal", value: "auto_paypal" },
                          ].find((opt) => opt.value === value)}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>
                      Account
                    </FormLabel>
                    <Controller
                      control={control}
                      name="paymentAccount"
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          ref={ref}
                          size="sm"
                          placeholder="Select account"
                          options={accountOptions}
                          value={accountOptions?.find(
                            (opt: any) => opt.value === value,
                          )}
                          onChange={(val: any) => onChange(val?.value)}
                        />
                      )}
                    />
                  </FormControl>
                  {watchPaymentType === "Installment" && (
                    <FormControl isRequired isInvalid={!!errors.emiCount}>
                      <FormLabel fontSize="xs" mb={1}>
                        Installments
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        placeholder="0"
                        {...register("emiCount", {
                          required: "Number of installments is required",
                          valueAsNumber: true,
                          validate: (v) =>
                            (v > 0 && Number.isInteger(v)) ||
                            "Must be a positive whole number",
                        })}
                      />
                      <FormErrorMessage color="red.500" fontSize="xs">
                        {errors.emiCount?.message?.toString()}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                  <FormControl isRequired isInvalid={!!errors.remark}>
                    <FormLabel fontSize="xs" mb={1}>
                      Remark
                    </FormLabel>
                    <Input
                      size="sm"
                      placeholder="Remark"
                      {...register("remark", { required: "Remark is required" })}
                    />
                    <FormErrorMessage color="red.500" fontSize="xs">
                      {errors.remark?.message?.toString()}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                {watchPaymentType === "Installment" && (
                  <Box
                    bg="blue.50"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.100"
                  >
                    <HStack spacing={4} mb={3}>
                      <FormControl maxW="150px">
                        <FormLabel fontSize="xs">Down Payment</FormLabel>
                        <Input
                          size="sm"
                          bg="white"
                          type="number"
                          {...register("downPayment")}
                        />
                      </FormControl>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        mt={6}
                        color="blue.600"
                      >
                        EMI Schedule:
                      </Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 4 }} gap={2}>
                      {fields.map((field, index) => (
                        <VStack
                          key={field.id}
                          bg="white"
                          p={2}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          align="start"
                          spacing={1}
                        >
                          <HStack justify="space-between" w="100%">
                            <Badge colorScheme="blue">
                              {watch(`installDetails.${index}.label`)}
                            </Badge>
                            {index === fields.length - 1 && (
                              <Badge colorScheme="purple">FINAL</Badge>
                            )}
                          </HStack>
                          <Input
                            size="xs"
                            type="date"
                            variant="flushed"
                            {...register(
                              `installDetails.${index}.installDate`
                            )}
                          />
                          <InputGroup size="xs">
                            <Input
                              type="number"
                              variant="filled"
                              fontWeight="bold"
                              isReadOnly={index === fields.length - 1}
                              {...register(`installDetails.${index}.amount`)}
                              onChange={(e) =>
                                updateManualEmi(index, e.target.value)
                              }
                            />
                          </InputGroup>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </CardBody>
            </Card>
          )}

          {/* KYC Documents - Only visible after lead selection or in edit mode */}
          {(selectedLead || isEditMode) && (
            <Card
              id="kyc-section"
              variant="outline"
              size="sm"
              bg="white"
              boxShadow="sm"
              borderColor={errors.kycDocument ? "red.400" : undefined}
            >
              <CardBody p={3}>
                <HStack mb={2} justify="space-between">
                  <HStack>
                    <FileText size={16} color="#D97706" />
                    <Heading size="xs">KYC Documents</Heading>
                  </HStack>
                  {errors.kycDocument && (
                    <Text color="red.500" fontSize="xs" fontWeight="medium">
                      {errors.kycDocument.message?.toString()}
                    </Text>
                  )}
                </HStack>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
                  {[
                    { name: "profilePic", label: "Profile Picture" },
                    { name: "frontAadhar", label: "Front Aadhar" },
                    { name: "backAadhar", label: "Back Aadhar" },
                    { name: "panCard", label: "PAN Card" },
                  ].map((doc) => {
                    const fileUrl = watch(doc.name as any);
                    return (
                      <Flex
                        key={doc.name}
                        align="center"
                        p={2}
                        border="1px dashed"
                        borderColor="gray.300"
                        borderRadius="md"
                        bg="white"
                      >
                        {fileUrl ? (
                          <HStack w="100%" justify="space-between">
                            <Image
                              src={fileUrl}
                              boxSize="30px"
                              borderRadius="sm"
                              objectFit="cover"
                            />
                            <Text fontSize="2xs" isTruncated maxW="80px">
                              {doc.label}
                            </Text>
                            <IconButton
                              aria-label="del"
                              icon={<X size={12} />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => setValue(doc.name, "")}
                            />
                          </HStack>
                        ) : (
                          <FormControl>
                            <FormLabel
                              htmlFor={doc.name}
                              m={0}
                              cursor="pointer"
                              w="100%"
                            >
                              <HStack justify="center" spacing={2}>
                                {isUploading === doc.name ? (
                                  <Spinner size="xs" />
                                ) : (
                                  <Upload size={14} />
                                )}
                                <Text fontSize="xs">{doc.label}</Text>
                              </HStack>
                            </FormLabel>
                            <Input
                              type="file"
                              id={doc.name}
                              display="none"
                              accept="image/*"
                              onChange={(e) => handleUpload(e, doc.name)}
                            />
                          </FormControl>
                        )}
                      </Flex>
                    );
                  })}
                </SimpleGrid>
              </CardBody>
            </Card>
          )}

          <Flex justify="flex-end" gap={3}>
            <Button size="sm" variant="ghost" onClick={handleBack}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
              loadingText={isEditMode ? "Updating..." : "Submitting..."}
              isDisabled={!isEditMode && !selectedLead}
              leftIcon={<Plus size={16} />}
            >
              {isEditMode ? "Update Admission" : "Submit Admission"}
            </Button>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};

export default AdmissionForm;
